import {format, sub} from 'date-fns';

import {CommitMutation} from '../../../../mutations/CommitMutation';
import {DuplicateTimeRegistrationsInDateRangeMutationResponse} from '../../../../mutations/time_registrations/__generated__/DuplicateTimeRegistrationsInDateRangeMutation.graphql';
import DuplicateTimeRegistrationsInDateRangeMutation from '../../../../mutations/time_registrations/DuplicateTimeRegistrationsInDateRangeMutation';
import {DateRange, DateRangeYMD, getWeekDateRangeFromDate} from '../../../shared/util/DateUtil';
import {notNullishAndPredicate} from '../../../shared/util/NotNullPredicate';
import {createToast} from '../../../shared/components/toasts/another-toast/toaster';
import Util from '../../../shared/util/util';

import type {ValidatedTimeRegRequestType, ValidatedTimeRegsInDateRangeType} from './CopyLastWeekValidationSection';

export const getPreviousWeekDateRangeFromDate = (selectedDate: Date): DateRange => {
	const oneWeekAgo = sub(selectedDate, {days: 7});
	return getWeekDateRangeFromDate(oneWeekAgo);
};

export const getPreviousDayDateRangeFromDate = (selectedDate: Date): DateRange => {
	const oneDayAgo = sub(selectedDate, {days: 1});

	return {startDate: oneDayAgo, endDate: oneDayAgo};
};

export const getCopyFromDateRange = (selectedDate: Date, copyFullWeek: boolean): DateRange => {
	return copyFullWeek ? getPreviousWeekDateRangeFromDate(selectedDate) : getPreviousDayDateRangeFromDate(selectedDate);
};

export const getCopyToDateRange = (selectedDate: Date, copyFullWeek: boolean): DateRange => {
	return copyFullWeek ? getWeekDateRangeFromDate(selectedDate) : {startDate: selectedDate, endDate: selectedDate};
};

export const copyWithoutDoneTasks = (
	personId: string,
	dateRangeYMD: DateRangeYMD,
	onSuccess: (mutationResult: DuplicateTimeRegistrationsInDateRangeMutationResponse) => void
): void => {
	CommitMutation(
		DuplicateTimeRegistrationsInDateRangeMutation,
		{
			...dateRangeYMD,
			personId,
			excludeDoneTasks: true,
		},
		onSuccess
	);
};

export const copyAll = (
	personId: string,
	dateRangeYMD: DateRangeYMD,
	onSuccess: (mutationResult: DuplicateTimeRegistrationsInDateRangeMutationResponse) => void
): void => {
	CommitMutation(
		DuplicateTimeRegistrationsInDateRangeMutation,
		{
			...dateRangeYMD,
			personId,
		},
		onSuccess
	);
};

export const dateRangeToText = (dateRange: DateRange): string => {
	if (dateRange.startDate === dateRange.endDate) {
		return format(dateRange.startDate, 'd MMM');
	}

	return `${format(dateRange.startDate, 'd MMM')} - ${format(dateRange.endDate, 'd MMM')}`;
};

export const getTimeRegistrationsByReason = (
	timeRegistrationValidations: ValidatedTimeRegsInDateRangeType,
	reason: string
): ValidatedTimeRegRequestType[] => {
	return timeRegistrationValidations
		.filter(
			notNullishAndPredicate(timeRegistrationValidation => timeRegistrationValidation.validationReasons.includes(reason))
		)
		.map(timeRegistrationValidation => timeRegistrationValidation.timeRegRequest);
};

export function getInvalidTimeRegistrations(
	timeRegistrationValidations: ValidatedTimeRegsInDateRangeType
): ValidatedTimeRegRequestType[] {
	return timeRegistrationValidations
		.filter(notNullishAndPredicate(timeRegistrationValidation => !timeRegistrationValidation.valid))
		.map(timeRegistrationValidation => timeRegistrationValidation.timeRegRequest);
}

export const getMinutesRegistered = (timeRegistrations: ValidatedTimeRegRequestType[]): number => {
	return timeRegistrations.reduce((acc, curr) => {
		return acc + curr.minutesRegistered;
	}, 0);
};
export const getBillableMinutesRegistered = (timeRegistrations: ValidatedTimeRegRequestType[]): number => {
	return timeRegistrations.reduce((acc, curr) => {
		return acc + (curr.billableMinutesRegistered ?? 0);
	}, 0);
};

export const ValidationReason = {
	NOT_ALLOWED_ON_DONE_TASKS: {
		singular: 'on a done task',
		plural: 'on done tasks',
	},
	NOT_ALLOWED_ON_PROJECT: {
		singular: 'at a project level',
		plural: 'at project level',
	},
	INTERNAL_TIME_NOT_ALLOWED: {
		singular: 'on an internal time category',
		plural: 'on internal time categories',
	},
	EXCEEDING_TASK_ESTIMATE: {
		singular: 'exceeding the task estimate',
		plural: 'exceeding the task estimate',
	},
	TASK_ASSIGNMENT_REQUIRED: {
		singular: 'on a task not assigned to you',
		plural: 'on tasks not assigned to you',
	},
	OUTSIDE_TASK_DATES: {
		singular: 'outside the start and end dates of its task',
		plural: 'outside the start and end dates of their tasks',
	},
	OUTSIDE_PROJECT_DATES: {
		singular: 'outside the start and end dates of its project',
		plural: 'outside the start and end dates of their project',
	},
	NOTE_REQUIRED: {
		singular: 'without a note',
		plural: 'without a note',
	},
};

function getCopyToastMessage(duplicatedTimeRegsAmount: number): string {
	if (duplicatedTimeRegsAmount === 0) {
		return `No time registrations have been copied`;
	} else if (duplicatedTimeRegsAmount === 1) {
		return `${duplicatedTimeRegsAmount} time registration has been copied`;
	} else {
		return `${duplicatedTimeRegsAmount} time registrations have been copied`;
	}
}

function onCopySuccess(mutationResponse: DuplicateTimeRegistrationsInDateRangeMutationResponse, closeModal: () => void): void {
	if (Util.checkForSageErrorAndShowModal(mutationResponse?.duplicateTimeRegsInDateRange?.errors, true)) {
		return;
	}
	const duplicatedTimeRegsAmount = mutationResponse.duplicateTimeRegsInDateRange?.timeRegs?.length || 0;
	createToast({
		duration: 5000,
		message: getCopyToastMessage(duplicatedTimeRegsAmount),
	});
	closeModal();
}

export function handleCopyAll(
	personId: string,
	copyDateRange: DateRangeYMD,
	onCopyCallback: () => void,
	closeModal: () => void
): void {
	onCopyCallback();
	copyAll(personId, copyDateRange, mutationResponse => onCopySuccess(mutationResponse, closeModal));
}

export function handleCopyWithoutDoneTasks(
	personId: string,
	copyDateRange: DateRangeYMD,
	onCopyCallback: () => void,
	closeModal: () => void
): void {
	onCopyCallback();
	copyWithoutDoneTasks(personId, copyDateRange, mutationResponse => onCopySuccess(mutationResponse, closeModal));
}
